export type EnrollmentResult = {
  filename: string;
  type: 'add' | 'remove';
  status: 'success' | 'failed' | 'skipped';
  error?: string;
};

export type EnrollmentModalState = {
  running: boolean;
  done: boolean;
  total: number;
  currentIndex: number;
  currentItem: { type: 'add' | 'remove'; preview: string; filename: string } | null;
  currentStage: string;
  results: EnrollmentResult[];
};

export type EnrollmentModalProps = {
  state: EnrollmentModalState;
  showCancelConfirm: boolean;
  onCancelClick: () => void;
  onCancelConfirm: () => void;
  onCancelDeny: () => void;
  onClose: () => void;
};

export default function EnrollmentModal({
  state,
  showCancelConfirm,
  onCancelClick,
  onCancelConfirm,
  onCancelDeny,
  onClose,
}: EnrollmentModalProps) {
  const { running, done, total, currentIndex, currentItem, currentStage, results } = state;

  const progress = total > 0 ? Math.round(((currentIndex) / total) * 100) : 0;
  const countLabel = done
    ? `${total} of ${total} processed`
    : `${(currentIndex ?? 0) + 1} of ${total}`;

  const enrolled  = results.filter((r) => r.type === 'add'    && r.status === 'success').length;
  const removed   = results.filter((r) => r.type === 'remove' && r.status === 'success').length;
  const failed    = results.filter((r) => r.status === 'failed');
  const skipped   = results.filter((r) => r.status === 'skipped').length;

  return (
    <div
      className="ai-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !running) onClose(); }}
    >
      <div className="ai-modal" role="dialog" aria-modal="true" aria-label="Enrolling Photos">
        <div className="ai-modal-header">
          <h2 className="ai-modal-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Enrolling Photos
          </h2>
          {!running && (
            <button className="ai-modal-close" onClick={onClose} aria-label="Close">×</button>
          )}
        </div>

        {currentItem && (
          <div className="ai-modal-current">
            <div className="ai-modal-thumb">
              <img src={currentItem.preview} alt={currentItem.filename} />
            </div>
            <div className="ai-modal-photo-info">
              <div className="ai-modal-filename">{currentItem.filename}</div>
              <div className="enroll-modal-type-badge">
                {currentItem.type === 'add' ? 'Adding' : 'Removing'}
              </div>
              <div className={`ai-modal-stage${done ? ' done' : ''}`}>{currentStage}</div>
            </div>
          </div>
        )}

        <div className="ai-modal-progress-row">
          <span className="ai-modal-count">{countLabel}</span>
        </div>
        <div className="ai-modal-progress-bar-track">
          <div
            className="ai-modal-progress-bar-fill"
            style={{ width: `${done ? 100 : progress}%` }}
          />
        </div>

        {done && (
          <div className="ai-modal-summary">
            {enrolled > 0 && (
              <div className="ai-modal-summary-item">
                <span className="ai-summary-value">{enrolled}</span>
                <span className="ai-summary-label">enrolled</span>
              </div>
            )}
            {removed > 0 && (
              <div className="ai-modal-summary-item">
                <span className="ai-summary-value">{removed}</span>
                <span className="ai-summary-label">removed</span>
              </div>
            )}
            {failed.length > 0 && (
              <div className="ai-modal-summary-item">
                <span className="ai-summary-value">{failed.length}</span>
                <span className="ai-summary-label">failed</span>
              </div>
            )}
            {skipped > 0 && (
              <div className="ai-modal-summary-item">
                <span className="ai-summary-value">{skipped}</span>
                <span className="ai-summary-label">skipped</span>
              </div>
            )}
            {failed.length > 0 && (
              <ul className="enroll-modal-failures">
                {failed.map((r, i) => (
                  <li key={i}>
                    <span className="enroll-failure-name">{r.filename}</span>
                    {r.error && <span className="enroll-failure-error"> — {r.error}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {showCancelConfirm && (
          <div className="ai-cancel-confirm">
            <p>Stop after this photo finishes? Photos processed so far will be kept.</p>
            <div className="ai-cancel-confirm-btns">
              <button className="ai-cancel-deny-btn" onClick={onCancelDeny}>Keep going</button>
              <button className="ai-cancel-confirm-btn" onClick={onCancelConfirm}>Stop</button>
            </div>
          </div>
        )}

        <div className="ai-modal-footer">
          {running && !showCancelConfirm && (
            <button className="ai-modal-cancel-btn" onClick={onCancelClick}>Cancel</button>
          )}
          {done && (
            <button className="ai-modal-done-btn" onClick={onClose}>Done</button>
          )}
        </div>
      </div>
    </div>
  );
}
